/**
 * Email confirmation page. Customer arrives here via the verification
 * email link; on success they're redirected to /signin.
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { FormError } from "@/components/FormError";
import { doConfirmSignUp } from "@/lib/auth/useAuth";

interface FormValues {
  code: string;
}

export function ConfirmSignUp() {
  const [params] = useSearchParams();
  const email = params.get("email") ?? "";
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { register, handleSubmit } = useForm<FormValues>();

  const onSubmit = async (values: FormValues) => {
    if (!email) {
      setServerError("Missing email address. Please use the link from your email.");
      return;
    }
    setSubmitting(true);
    setServerError(null);
    const result = await doConfirmSignUp(email, values.code);
    setSubmitting(false);
    if (!result.ok) {
      setServerError(result.error ?? "Confirmation failed");
      return;
    }
    navigate("/signin");
  };

  return (
    <div className="mx-auto max-w-md px-5 py-10 md:py-16">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Verify email
      </p>
      <h1 className="mb-3 font-serif text-3xl text-starz-navy md:text-4xl">
        Check your <em className="text-starz-gold">inbox</em>.
      </h1>
      <div className="mb-7 h-0.5 w-14 bg-starz-gold" aria-hidden="true" />
      <p className="mb-6 text-sm text-warm-gray">
        We sent a verification code to <strong>{email}</strong>. Enter it below to activate your
        account.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-warm-gray">
            Code
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            {...register("code", { required: true })}
            className="w-full rounded-lg border border-starz-gold/25 bg-white px-4 py-2.5 text-starz-charcoal focus:outline-none focus:ring-2 focus:ring-starz-gold focus:ring-offset-2"
          />
        </label>
        <FormError message={serverError} />
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-starz-navy px-5 py-3 font-medium text-starz-off-white hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? "Verifying…" : "Verify and continue"}
        </button>
      </form>
    </div>
  );
}
